/** Operations → Safety meeting: supervisor (foreman) countersigns today's record; printed on the exported/emailed PDF. */
import { useCallback, useEffect, useState } from "react";
import { PenLine, ShieldCheck, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SignaturePad from "./SignaturePad";

const getLocation = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({}), { timeout: 4000, maximumAge: 60000 });
});

export default function SafetySignoff({ canDeleteAny = false }) {
  const [data, setData] = useState(null);
  const [sig, setSig] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(false);
  const onSig = useCallback((v) => setSig(v), []);

  const load = async () => { try { const { data } = await api.get("/safety/signoff"); setData(data); } catch { setData({ confirmed: [], missing: [], signoffs: [] }); } };
  useEffect(() => { load(); }, []);

  const sign = async () => {
    if (!sig) return toast.error("Draw your signature");
    setBusy(true);
    try {
      await api.post("/safety/signoff", { signature: sig, note, ...(await getLocation()) });
      toast.success("Signed — it will print on today's PDF record");
      setSigning(false); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not sign"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api.delete(`/safety/signoff/${id}`); load(); } catch { toast.error("Could not remove"); } };

  if (!data) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs" data-testid="safety-signoff">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-400" />
        <div className="mr-auto">
          <div className="text-sm font-bold text-white">Supervisor sign-off · {data.day}</div>
          <div className="text-white/60" data-testid="safety-signoff-summary">{data.confirmed.length} confirmed{data.missing.length ? ` · ${data.missing.length} not yet: ${data.missing.slice(0, 6).join(", ")}${data.missing.length > 6 ? "…" : ""}` : " · everyone confirmed"}</div>
        </div>
        {!signing && !data.mine && <button onClick={() => setSigning(true)} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 font-semibold text-white" data-testid="safety-signoff-start"><PenLine className="h-3.5 w-3.5" /> Countersign today</button>}
        {data.mine && !signing && <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 font-semibold text-emerald-200" data-testid="safety-signoff-done">You signed at {new Date(data.mine.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
      </div>

      {signing && (
        <div className="mt-3 space-y-2" data-testid="safety-signoff-form">
          <p className="text-white/70">I confirm the crew listed above watched today's safety meeting video and I reviewed the hazards with them.</p>
          <SignaturePad open={signing} onChange={onSig} testid="safety-signoff-pad" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. reviewed trench shoring, PPE check done" className="w-full rounded-md border border-white/15 bg-white/5 px-2 py-1.5 text-white" data-testid="safety-signoff-note" />
          <div className="flex gap-2">
            <button onClick={() => setSigning(false)} className="rounded-md border border-white/15 px-3 py-1.5 font-semibold text-white/70">Cancel</button>
            <button onClick={sign} disabled={busy || !sig} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 font-semibold text-white disabled:opacity-50" data-testid="safety-signoff-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />} Sign record</button>
          </div>
        </div>
      )}

      {data.signoffs.length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="safety-signoff-list">
          {data.signoffs.map((so) => (
            <div key={so.signoff_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white p-2 text-gray-900">
              <img src={so.signature} alt={`${so.name} signature`} className="h-10 w-28 object-contain" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{so.name} <span className="font-normal text-gray-500">({so.role})</span></div>
                <div className="text-[11px] text-gray-500">{new Date(so.at).toLocaleString()}{so.note ? ` · ${so.note}` : ""}</div>
              </div>
              {(canDeleteAny || so.user_id === data.mine?.user_id) && <button onClick={() => remove(so.signoff_id)} className="text-gray-400 hover:text-red-600" aria-label="Remove sign-off" data-testid={`safety-signoff-remove-${so.signoff_id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
